import { SectionHeader } from "@dash/page";
import { QuerySkeleton } from "@dash/skeleton";
import { RowCellContent, Table, TableBody, TableHeadColumn } from "@dash/table";
import { useTheme } from "@emotion/react";
import { formatCurrency } from "@legacy-megarax/react-utils";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { MdErrorOutline, MdExpand, MdExpandLess, MdExpandMore } from "react-icons/md";

import { IamAuthContext } from "@megaron/auth-react";
import { ItemDoc } from "@megaron/catalog-contracts";
import { OrderShippingPreview, OrderShippingPreviewInput } from "@megaron/crm-contracts";
import { useClientManager } from "@megaron/react-clients";

import { formatShippingUnitName } from "./formatShippingUnitName";
import { calculateShippingPalletFactor } from "./calculateShippingPalletFactor";

type Props = {
  source: OrderShippingPreviewInput;
  refreshKey?: string;
  itemNames?: Record<string, string>;
};

export const OrderShippingSection: React.FC<Props> = (props) => {
  const auth = useContext(IamAuthContext);
  const roles = auth.iamUser?.roles ?? [];
  const hasShippingRole = roles.includes("shipping.user") || roles.includes("shipping.admin");

  if (!hasShippingRole) return null;

  return <OrderShippingPreviewSection {...props} />;
};

const OrderShippingPreviewSection: React.FC<Props> = ({ source, refreshKey, itemNames }) => {
  const shippingPreviewQuery = useClientManager("crm").orderShippingPreview().useQuery(source, {
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const itemsQuery = useClientManager("docs").searchItems().useQuery(
    {},
    {
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  );
  const previousRefreshKey = useRef(refreshKey);

  useEffect(() => {
    if (previousRefreshKey.current === refreshKey) return;

    previousRefreshKey.current = refreshKey;
    void shippingPreviewQuery.refetch();
  }, [refreshKey, shippingPreviewQuery.refetch]);

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: "1rem", width: "100%" }}>
      <SectionHeader isHr>Wysyłka</SectionHeader>
      <QuerySkeleton
        query={shippingPreviewQuery}
        height="220px"
        width="100%"
        onRetry={() => shippingPreviewQuery.refetch()}
        errorScreen={(error) => <ShippingPreviewError error={error} />}
      >
        {(preview) => (
          <ShippingPreview
            solution={preview.solution}
            currency={preview.currency}
            itemNames={itemNames}
            catalogItems={itemsQuery.data?.items}
            isLoading={shippingPreviewQuery.isFetching || itemsQuery.isFetching}
          />
        )}
      </QuerySkeleton>
    </div>
  );
};

const ShippingPreview = ({
  solution,
  currency,
  itemNames = {},
  catalogItems,
  isLoading,
}: Pick<OrderShippingPreview, "solution" | "currency"> & {
  itemNames?: Record<string, string>;
  catalogItems?: ItemDoc[];
  isLoading: boolean;
}) => {
  const theme = useTheme();
  const itemsById = useMemo(() => new Map(catalogItems?.map((item) => [item.itemId, item])), [catalogItems]);
  const getPalletFactor = (quantities: Partial<Record<string, number>>): string => {
    if (!catalogItems) return "—";
    const items = Object.entries(quantities).flatMap(([unit, quantity]) => {
      const item = itemsById.get(unit);
      return item && quantity ? [{ item, quantity }] : [];
    });
    return items.length ? calculateShippingPalletFactor(items).toFixed(2) : "—";
  };
  const [expandedUnitIds, setExpandedUnitIds] = useState<Set<string>>(new Set());
  const unallocatedItems = Object.entries(solution.unallocated).filter(([, quantity]) => (quantity ?? 0) > 0);
  const unallocatedCellCss = { background: theme.colors.dangerBackground, color: theme.colors.danger };

  const columns: TableHeadColumn<string>[] = [
    { label: "Jednostka", isSortable: false },
    { label: "Współczynnik paletowy", isSortable: false, cellCss: { textAlign: "right" } },
    { label: "Koszt", isSortable: false, cellCss: { textAlign: "right" } },
  ];

  const topUnitRows = solution.topUnits.flatMap((topUnit, index) => {
    const id = `${topUnit.unit}-${index}`;
    const isExpanded = expandedUnitIds.has(id);
    const quantityLabel = topUnit.quantity > 1 ? <span css={{ fontWeight: 700 }}>{topUnit.quantity} × </span> : null;
    const quantities = { ...topUnit.inputs };
    for (const [unit, quantity] of Object.entries(topUnit.filler)) {
      quantities[unit] = (quantities[unit] ?? 0) + (quantity ?? 0);
    }

    const cellsContent: RowCellContent[] = [
      {
        element: (
          <div css={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <span>
              {quantityLabel}
              {itemNames[topUnit.unit] ?? formatShippingUnitName(topUnit.unit)}
            </span>
            {isExpanded ? <MdExpandLess /> : <MdExpandMore />}
          </div>
        ),
      },
      {
        element: getPalletFactor(quantities),
        cellCss: { textAlign: "right" },
      },
      {
        element: (
          <span
            css={{
              color: topUnit.cost.gt(0) ? theme.colors.danger : theme.colors.secondaryText,
              fontWeight: topUnit.cost.gt(0) ? 700 : 400,
            }}
          >
            {formatCurrency(topUnit.cost, currency)}
          </span>
        ),
        cellCss: { textAlign: "right" },
      },
    ];

    const topUnitRow = {
      id,
      cellsContent,
      onClick: () =>
        setExpandedUnitIds((expandedIds) => {
          const nextIds = new Set(expandedIds);
          if (nextIds.has(id)) nextIds.delete(id);
          else nextIds.add(id);
          return nextIds;
        }),
    };

    if (!isExpanded) return [topUnitRow];

    return [
      topUnitRow,
      ...unitTotalRows(id, topUnit.inputs, "towar", theme.colors.primary, itemNames),
      ...unitTotalRows(id, topUnit.filler, "wypełnienie", theme.colors.secondaryText, itemNames),
    ];
  });

  const unallocatedRows: { id: string; cellsContent: RowCellContent[] }[] = unallocatedItems.map(
    ([itemId, quantity]) => ({
      id: `unallocated-${itemId}`,
      cellsContent: [
        {
          element: (
            <div css={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <MdErrorOutline size={18} />
              <span>{itemNames[itemId] ?? itemId}</span>
            </div>
          ),
          cellCss: unallocatedCellCss,
        },
        { element: quantity ?? 0, cellCss: { ...unallocatedCellCss, textAlign: "right" } },
        { element: "—", cellCss: { ...unallocatedCellCss, textAlign: "right" } },
      ],
    }),
  );

  const rows = [...unallocatedRows, ...topUnitRows];

  return (
    <div aria-busy={isLoading}>
      <div css={{ overflow: "auto" }}>
        <Table columns={columns} hideHeader>
          <TableBody rows={rows} isLoading={isLoading} />
        </Table>
      </div>
      <div
        css={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "flex-end",
          gap: "0.5rem 1.5rem",
          padding: "0.75rem",
          opacity: isLoading ? 0.4 : 1,
        }}
      >
        <strong css={{ color: solution.totalCost.isZero() ? theme.colors.success : theme.colors.danger }}>
          Razem: {formatCurrency(solution.totalCost, currency)}
        </strong>
      </div>
    </div>
  );
};

const unitTotalRows = (
  topUnitId: string,
  quantities: Partial<Record<string, number>>,
  type: string,
  color: string,
  itemNames: Record<string, string> = {},
): { id: string; cellsContent: RowCellContent[] }[] =>
  Object.entries(quantities).map(([unit, quantity]) => {
    return {
      id: `${topUnitId}-${type}-${unit}`,
      cellsContent: [
        {
          element: (
            <span css={{ paddingLeft: "1.5rem", color }}>↳ {itemNames[unit] ?? formatShippingUnitName(unit)}</span>
          ),
        },
        { element: quantity ?? 0, cellCss: { textAlign: "right", color } },
        { element: "", cellCss: { textAlign: "right" } },
      ],
    };
  });

const ShippingPreviewError = ({ error }: { error: unknown }) => {
  if (error === "NoSolution") return <>Nie znaleziono konfiguracji wysyłki.</>;
  if (error === "ShippingPriceListNotFound") return <>Nie skonfigurowano cennika wysyłki.</>;

  return <>Nie udało się wyliczyć wysyłki.</>;
};
